import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, Mail, ArrowUpRight } from "lucide-react";

const LINKS = [
    { to: "/", label: "Home", id: "home" },
    { to: "/shop", label: "Collection", id: "shop" },
    { to: "/about", label: "About", id: "about" },
    { to: "/contact", label: "Contact", id: "contact" },
    { to: "/account", label: "Account", id: "account" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [logoOk, setLogoOk] = useState(true);

    useEffect(() => {
        const f = () => setScrolled(window.scrollY > 30);
        f();
        window.addEventListener("scroll", f, { passive: true });
        return () => window.removeEventListener("scroll", f);
    }, []);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    return (
        <>
            <header className="fixed inset-x-0 top-0 z-[60]">
                <div
                    className={`hidden items-center justify-between overflow-hidden bg-black/60 px-6 backdrop-blur transition-all duration-500 md:flex lg:px-10 ${
                        scrolled ? "h-0 opacity-0" : "h-9 opacity-100"
                    }`}
                >
                    <div className="flex items-center gap-6 text-[11px] font-mono uppercase tracking-[0.15em] text-mute">
                        <a href="tel:9738002530" className="flex items-center gap-2 transition-colors hover:text-gold">
                            <Phone size={12} /> 97380 02530
                        </a>
                        <a href="mailto:info@urwan.com" className="flex items-center gap-2 transition-colors hover:text-gold">
                            <Mail size={12} /> info@urwan.com
                        </a>
                    </div>
                    <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-gold">
                        Complimentary shipping across India
                    </p>
                </div>
                <div
                    data-testid="navbar"
                    className={`flex items-center justify-between px-5 transition-all duration-500 md:px-8 lg:px-10 ${
                        scrolled
                            ? "h-16 border-b border-white/10 bg-black/80 backdrop-blur-xl"
                            : "h-20 bg-gradient-to-b from-black/70 to-transparent"
                    }`}
                >
                    <Link to="/" data-testid="nav-logo" className="flex items-center gap-3" aria-label="URWAN home">
                        {logoOk ? (
                            <img
                                src="/img/brand/logo.png"
                                alt="URWAN"
                                onError={() => setLogoOk(false)}
                                className="h-8 w-auto md:h-9"
                            />
                        ) : (
                            <span className="font-display text-2xl font-extrabold tracking-tight text-ink">
                                URWAN
                            </span>
                        )}
                    </Link>
                    <nav className="hidden items-center gap-9 lg:flex">
                        {LINKS.map((l) => (
                            <NavLink
                                key={l.id}
                                to={l.to}
                                data-testid={`nav-link-${l.id}`}
                                className={({ isActive }) =>
                                    `group relative font-mono text-xs uppercase tracking-[0.2em] transition-colors ${
                                        isActive ? "text-gold" : "text-mute hover:text-ink"
                                    }`
                                }
                            >
                                {({ isActive }) => (
                                    <>
                                        {l.label}
                                        <span
                                            className={`absolute -bottom-1.5 left-0 h-px w-full origin-left bg-gold transition-transform duration-300 ${
                                                isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                                            }`}
                                        />
                                    </>
                                )}
                            </NavLink>
                        ))}
                    </nav>
                    <div className="flex items-center gap-3">
                        <Link
                            to="/shop"
                            data-testid="nav-shop-now"
                            className="hidden items-center gap-2 border border-gold/60 px-5 py-2.5 font-display text-xs font-bold uppercase tracking-[0.2em] text-gold transition-all hover:bg-gold hover:text-black sm:inline-flex"
                        >
                            Shop Now <ArrowUpRight size={14} />
                        </Link>
                        <button
                            data-testid="nav-mobile-toggle"
                            onClick={() => setOpen(true)}
                            aria-label="Open menu"
                            className="rivet grid h-12 w-12 place-items-center rounded-full lg:hidden"
                        >
                            <Menu size={17} className="relative z-10 text-[#3c1e06]" />
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35 }}
                        data-testid="mobile-menu"
                        className="fixed inset-0 z-[80] flex flex-col bg-obsidian/[0.98] backdrop-blur-xl"
                    >
                        <div className="flex h-20 items-center justify-between px-5 md:px-8">
                            <img src="/img/brand/logo.png" alt="URWAN" className="h-8 w-auto" onError={(e) => { e.target.style.display = "none"; }} />
                            <button
                                data-testid="nav-mobile-close"
                                onClick={() => setOpen(false)}
                                aria-label="Close menu"
                                className="grid h-11 w-11 place-items-center border border-white/15 text-ink"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <nav className="flex flex-1 flex-col justify-center gap-1 px-6">
                            {LINKS.map((l, i) => (
                                <motion.div
                                    key={l.id}
                                    initial={{ opacity: 0, x: -32 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.08 + i * 0.07, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                                >
                                    <NavLink
                                        to={l.to}
                                        data-testid={`mobile-link-${l.id}`}
                                        onClick={() => setOpen(false)}
                                        className={({ isActive }) =>
                                            `block py-2 font-display text-3xl font-extrabold uppercase tracking-tight transition-colors ${
                                                isActive ? "text-gold" : "text-ink hover:text-gold"
                                            }`
                                        }
                                    >
                                        {l.label}
                                    </NavLink>
                                </motion.div>
                            ))}
                        </nav>
                        <div className="border-t border-white/10 px-6 py-6 text-[11px] font-mono uppercase tracking-[0.15em] text-mute">
                            <p>97380 02530 · info@urwan.com</p>
                            <p className="mt-2">Whitefield, Bangalore — 560066</p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
